/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core-internal';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { readAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CustomersDataActions.g';
import { createObservableDataAction } from '../create-data-action.vnext';

/**
 * Input class for get customer data action.
 */
export class GetCustomerInput implements IActionInput {
    public customerAccountNumber?: string;

    private readonly apiSettings: ICommerceApiSettings;

    private readonly cacheType?: CacheType;

    public constructor(apiSettings: ICommerceApiSettings, accountNumber?: string, cacheType?: CacheType) {
        this.customerAccountNumber = accountNumber;
        this.apiSettings = apiSettings;
        this.cacheType = cacheType;
    }

    /**
     * GetCacheKey.
     * @returns - Returns string.
     */
    public getCacheKey = (): string => `Customer-${this.customerAccountNumber}-chanId:${this.apiSettings.channelId}`;

    /**
     * GetCacheObjectType.
     * @returns - Cache Object Type string.
     */
    public getCacheObjectType = (): string => 'Customer';

    /**
     * DataCacheType.
     * @returns - CacheType string.
     */
    public dataCacheType = (): CacheType => {
        return this.cacheType ? this.cacheType : 'request';
    };
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData -- The input data passed to the createInput method.
 * @returns - GetCustomer input.
 */
export const createGetCustomerInput = (inputData: ICreateActionContext<IGeneric<IAny>>): GetCustomerInput => {
    const { user, apiSettings } = inputData.requestContext;
    if (!user.token || !user.isAuthenticated) {
        throw new Error('[getCustomer][createInput]: User is not Authenticated.');
    }

    return new GetCustomerInput(apiSettings);
};

/**
 * GetCustomerAction.
 * @param input - GetCustomerInput.
 * @param ctx - Action Context.
 * @returns - Customer information.
 */
const getCustomerAction = async (input: GetCustomerInput, ctx: IActionContext): Promise<Customer> => {
    if (input && ctx.requestContext.user.isAuthenticated) {
        try {
            return readAsync({ callerContext: ctx, bypassCache: 'get' }, '').then(response => {
                return Array.isArray(response) ? response[0] : response;
            });
        } catch (error) {
            ctx.telemetry.error(`Error running getCustomer action: ${error}`);
            return <Customer>{};
        }
    }

    ctx.telemetry.warning('[getCustomer]Invalid input passed to action');
    return <Customer>{};
};

/**
 * The customer data action.
 */
export const getCustomerActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce/get-customer',
    action: <IAction<Customer>>getCustomerAction,
    input: createGetCustomerInput
});

export default getCustomerActionDataAction;
